import React from 'react';
import { Box, Typography, Button, IconButton, Chip, Tooltip as MuiTooltip } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import { getEventIconColor, getDurationAlertColor } from '../utils/sampleResponses';

// Format timestamp for display
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format detail key for display (camelCase to Title Case)
const formatDetailKey = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Get alert icon based on type
const AlertIcon = ({ type, size = 'small' }) => {
  const iconProps = { fontSize: size };
  switch (type) {
    case 'warning':
      return <WarningAmberIcon {...iconProps} />;
    case 'error':
      return <ErrorOutlineIcon {...iconProps} />;
    case 'info':
    default:
      return <InfoOutlinedIcon {...iconProps} />;
  }
};

// Timeline Header Component
const TimelineHeader = ({ timeline, colors, onTimelineAction }) => {
  const { title, subtitle, currentState, actions } = timeline;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Title row with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {title}
        </Typography>
        {actions && actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions.slice(0, 2).map((action) => (
              <Button
                key={action.actionId}
                variant="outlined"
                size="small"
                onClick={() => onTimelineAction(action)}
                sx={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                  borderRadius: 0,
                  '&:hover': {
                    borderColor: '#22c55e',
                    color: '#22c55e',
                    bgcolor: 'rgba(34, 197, 94, 0.08)',
                  },
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Subtitle */}
      {subtitle && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
          {subtitle}
        </Typography>
      )}

      {/* Current State */}
      {currentState && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}>
          <Chip
            label={currentState.status}
            size="small"
            sx={{
              bgcolor: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              fontWeight: 600,
              borderRadius: 0,
            }}
          />
          {currentState.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: colors.textMuted }} />
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {currentState.location}
              </Typography>
            </Box>
          )}
          {currentState.lastUpdated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: colors.textMuted }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {formatTimestamp(currentState.lastUpdated)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// Timeline Summary Component
const TimelineSummary = ({ summary, colors }) => {
  if (!summary) return null;

  return (
    <Box sx={{
      display: 'flex',
      gap: 3,
      p: 1.5,
      mb: 2,
      bgcolor: colors.surfaceElevated,
      border: `1px solid ${colors.border}`,
    }}>
      <Box>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
          Total Events
        </Typography>
        <Typography variant="body1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {summary.totalEvents}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
          Duration
        </Typography>
        <Typography variant="body1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {summary.totalDuration?.formatted || '-'}
        </Typography>
      </Box>
      {summary.alertCount > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
            Alerts
          </Typography>
          <Typography variant="body1" sx={{ color: '#f59e0b', fontWeight: 600 }}>
            {summary.alertCount}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Duration Connector Component
const DurationConnector = ({ durationToNext, colors }) => {
  if (!durationToNext) return null;

  const alertColors = durationToNext.alert ? getDurationAlertColor(durationToNext.alert.type) : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: '19px', my: 0.5 }}>
      {/* Vertical line */}
      <Box sx={{
        width: 2,
        height: 24,
        bgcolor: alertColors ? alertColors.border : colors.borderLight,
      }} />

      {/* Duration badge */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        ml: 1.5,
        px: 1,
        py: 0.25,
        bgcolor: alertColors ? alertColors.bg : colors.surface,
        border: `1px dashed ${alertColors ? alertColors.border : colors.borderLight}`,
      }}>
        <Typography variant="caption" sx={{
          color: alertColors ? alertColors.text : colors.textMuted,
          fontWeight: 500,
        }}>
          {durationToNext.formatted}
        </Typography>
        {durationToNext.alert && (
          <MuiTooltip title={durationToNext.alert.message} arrow>
            <Box sx={{ display: 'flex', color: alertColors.text }}>
              <AlertIcon type={durationToNext.alert.type} size="inherit" />
            </Box>
          </MuiTooltip>
        )}
      </Box>

      {/* Alert message */}
      {durationToNext.alert && (
        <Typography variant="caption" sx={{
          ml: 1,
          color: alertColors.text,
          fontStyle: 'italic',
        }}>
          {durationToNext.alert.message}
        </Typography>
      )}
    </Box>
  );
};

// Related Entities Component
const RelatedEntities = ({ entities, colors, onRelatedEntityClick }) => {
  if (!entities || entities.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {entities.map((entity, index) => (
        <Chip
          key={`${entity.type}-${entity.id}-${index}`}
          label={entity.label}
          size="small"
          icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
          onClick={() => onRelatedEntityClick(entity)}
          sx={{
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            color: '#3b82f6',
            borderRadius: 0,
            cursor: 'pointer',
            '& .MuiChip-icon': {
              color: '#3b82f6',
            },
            '&:hover': {
              bgcolor: 'rgba(59, 130, 246, 0.2)',
            },
          }}
        />
      ))}
    </Box>
  );
};

// Event Actions Component
const EventActions = ({ actions, colors, onEventAction }) => {
  if (!actions || actions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
      {actions.map((action) => (
        <Button
          key={action.actionId}
          variant="text"
          size="small"
          onClick={() => onEventAction(action)}
          sx={{
            color: '#22c55e',
            p: 0.5,
            minWidth: 'auto',
            fontSize: '0.75rem',
            '&:hover': {
              bgcolor: 'rgba(34, 197, 94, 0.08)',
            },
          }}
        >
          {action.label}
        </Button>
      ))}
    </Box>
  );
};

// Single Timeline Event Component
const TimelineEvent = ({ event, isLast, colors, onEventAction, onRelatedEntityClick }) => {
  const iconColor = getEventIconColor(event.iconColor);

  return (
    <Box>
      {/* Event row */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Event icon/dot */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 40,
        }}>
          <Box sx={{
            width: event.isFinal ? 16 : 12,
            height: event.isFinal ? 16 : 12,
            borderRadius: '50%',
            bgcolor: iconColor,
            border: event.isFinal ? `3px solid ${iconColor}40` : 'none',
            boxShadow: event.isFinal ? `0 0 0 4px ${iconColor}20` : 'none',
          }} />
        </Box>

        {/* Event content */}
        <Box sx={{ flex: 1, pb: isLast ? 0 : 1 }}>
          {/* Label and timestamp row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{
                color: colors.textPrimary,
                fontWeight: 600,
              }}>
                {event.label}
              </Typography>
              {event.isFinal && (
                <Chip
                  label="FINAL"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(34, 197, 94, 0.15)',
                    color: '#22c55e',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 18,
                    borderRadius: 0,
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>

          {/* Event details */}
          {event.details && Object.keys(event.details).length > 0 && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              mt: 0.5,
            }}>
              {Object.entries(event.details).map(([key, value]) => (
                <Box key={key}>
                  <Typography variant="caption" component="span" sx={{ color: colors.textMuted }}>
                    {formatDetailKey(key)}:
                  </Typography>{' '}
                  <Typography variant="caption" component="span" sx={{ color: colors.textSecondary }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Related entities */}
          <RelatedEntities
            entities={event.relatedEntities}
            colors={colors}
            onRelatedEntityClick={onRelatedEntityClick}
          />

          {/* Event actions */}
          <EventActions
            actions={event.actions}
            colors={colors}
            onEventAction={onEventAction}
          />
        </Box>
      </Box>

      {/* Duration connector to next event */}
      {!isLast && event.durationToNext && (
        <DurationConnector
          durationToNext={event.durationToNext}
          colors={colors}
        />
      )}
    </Box>
  );
};

// Main Timeline Renderer Component
const TimelineRenderer = ({
  timeline,
  colors,
  onEventAction,
  onTimelineAction,
  onRelatedEntityClick,
  onPinToBoard,
  sourceQuery,
}) => {
  if (!timeline) return null;

  const handleEventAction = (action) => {
    if (onEventAction) {
      onEventAction(action.sentence);
    }
  };

  const handleTimelineAction = (action) => {
    if (onTimelineAction) {
      onTimelineAction(action.sentence);
    }
  };

  const handleRelatedEntityClick = (entity) => {
    if (onRelatedEntityClick) {
      onRelatedEntityClick(entity);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header with title, subtitle, current state, and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <TimelineHeader
            timeline={timeline}
            colors={colors}
            onTimelineAction={handleTimelineAction}
          />
        </Box>

        {/* Pin to Dashboard button */}
        {onPinToBoard && (
          <MuiTooltip title="Pin to Dashboard" arrow>
            <IconButton
              size="small"
              onClick={() => onPinToBoard('TIMELINE', { timeline }, sourceQuery)}
              sx={{
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
                p: 0.5,
                ml: 1,
                '&:hover': {
                  color: '#22c55e',
                  borderColor: '#22c55e',
                  bgcolor: 'rgba(34, 197, 94, 0.08)',
                },
              }}
            >
              <PushPinIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        )}
      </Box>

      {/* Summary */}
      <TimelineSummary summary={timeline.summary} colors={colors} />

      {/* Events list */}
      <Box sx={{
        p: 2,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}>
        {timeline.events?.map((event, index) => (
          <TimelineEvent
            key={event.eventId}
            event={event}
            isLast={index === timeline.events.length - 1}
            colors={colors}
            onEventAction={handleEventAction}
            onRelatedEntityClick={handleRelatedEntityClick}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TimelineRenderer;
